import React from 'react';
import { Link } from 'react-router-dom';
import ListButton from '../components/elements/ListButton';
import { __, __n } from 'i18n-for-browser';
import { ministryIcons, icons, belastingIcons, beleidskeuzes } from '../assets/assets.js';
import Defines from '../Defines';
import BudgetIcon from "../assets/img/icons/budget.svg";
import Grondstoffen from '../assets/img/icons/grondstoffen.svg';
import LoanLand from "../assets/img/icons/lenen_land.svg";
import LoanToLand from "../assets/img/icons/uitlenen_land.svg";
import Bank from "../assets/img/icons/bank.svg";
import Gift from "../assets/img/icons/schenken_land.svg";
import { groupIcons } from '../assets/assets';
import config from "../config";
import { getCountryIcon } from "./bankHelper";

/**
* Feedback sorting algorithm
*/
function getOrderIndex(fb) {
  if (fb.oe) {
    //Effect
    return 3;
  }
  if (fb.type === "ALGEMEEN" || fb.type === "RAMP") {
    //Budgetkeuze
    return 1;
  }
  if (fb.type === "BELEIDSKEUZE") {
    return 2;
  }
  return 3;
}

const sorter = (a, b) => {
  const valA = getOrderIndex(a);
  const valB = getOrderIndex(b);
  if (valA < valB) {
    return -1;
  }
  if (valA > valB) {
    return 1;
  }
  return 0;
};

/**
 * get feedback by id (omschrijving)
 */
function getFeedback(feedbacks = [], feedbackId) {
  const item = feedbacks.find((fb) => {
    return fb.omschrijving === feedbackId;
  });
  return { ...item };
}

/**
 * Method to check if a feedback item has been read or not
 * @param {object} feedback
 * @param {bool} oe
 * @param {number} oe_index
 * @param {bool} economie
 * @return {bool}
 */
function checkRead(feedback, oe = false, oe_index = 0, economie = false) {
  // Invalid feedback, mark as read because there is no feedback to read
  if (!feedback.id && !feedback.reference) {
    return true;
  }

  if (oe) {
    return feedback.read['readOe_' + oe_index];
  }

  if (economie) {
    return feedback.read.economie ? feedback.read.economie : false;
  }

  return feedback.read.read;
}

function subString(string = '', maxLength, tail) {
  let maxString = string.substr(0, maxLength);
  maxString = maxString.substr(0, Math.min(maxString.length, maxString.lastIndexOf(" ")));
  return maxString + (string.length > maxLength ? tail : "");
}

/**
 * Method that takes an array of feedback
 * as input and reduces it to a single unread
 * feedback count.
 * @param {Array<{ read: { read: Bool } }>} feedback
 * @param {bool} economie
 * @returns {Number} - count
 */
function unreadCount(feedback = [], economie, isPolicy = false) {
  return feedback.reduce((count, fb) => {
    if (!economie) {
      if (!checkRead(fb)) {
        count += 1;
      }
    }

    if (!economie && !isPolicy) {
      fb.overigeEffecten.forEach((oe, i) => {
        if (!checkRead(fb, true, i)) {
          count += 1;
        }
      });
    }

    if (fb.economie && !isPolicy) {
      if (!checkRead(fb, false, 0, true)) {
        count += 1;
      }
    }

    return count;
  }, 0);
}

function getCollaborationPoints(ranges, collabs) {
  const range = ranges.find((range) => {
    const r = range.match(/(\d-\d)/g);
    const val = r && r[0] ? r[0].split('-') : null;
    if (Array.isArray(val)) {
      return parseInt(val[0], 10) <= collabs || collabs <= parseInt(val[1], 10);
    }
    return false;
  });
  if (range) {
    return parseInt(range.split('=')[1], 10) || 0;
  }
  return 0;
}

/**
 * Method to filter and map feedback items which are relevant for your ministry
 * @param {array<{}>} feedback
 * @param {string} ministry
 * @param {object} gameState
 * @return {array<{}>} relevant feedback
 */
function getMinistryFeedbackItems(feedback = [], ministry, gameState, gameStateInput) {
  return feedback
    .filter(fb => fb.ministerie === ministry)
    .map((fb) => {
      let icon = ministryIcons[fb.ministerie],
        to = '/ministries/' + fb.ministerie.toLowerCase() + '/' + fb.omschrijving + '/',
        points = fb.puntenscore,
        title = __(getFeedbackTitle(fb));
      if (Array.isArray(points) && points[0].match(/^SW\d-\d=\d*$/)) {
        points = getCollaborationPoints(points, getCollaborations(fb, gameStateInput).length);
      }
      if (fb.type === 'BELEIDSKEUZE') {
        title += fb[`titel_beleidskeuze_${gameState.language}`] ? `: ${fb[`titel_beleidskeuze_${gameState.language}`]}` : '';
        icon = icons.beleidskeuze;
      }
      if (fb.type === 'ALGEMEEN' || fb.type === 'RAMP') {
        icon = icons.globas;
        points = gameState.prevRound.gameStateSum[`${ministry}_points_raw`];
      }
      if (fb.type === 'STARTSITUATIE') {
        icon = null;
      }

      return {
        ...fb,
        icon,
        to,
        points,
        title,
      };
    });
}

function getOverigeEffecten(feedback = [], ministry) {
  return feedback.reduce((effects, fb) => {
    if (fb.overigeEffecten.length) {
      fb.overigeEffecten.forEach((oe, i) => {
        let title = `${__('sim_effect_vanuit')}: `;
        let icon = ministryIcons[fb.ministerie];
        if (fb.belastingNaam) {
          title += __(fb.belastingsoort);
          icon = belastingIcons[fb.belastingsoort];
        } else {
          title += __(fb.ministerie);
        }

        if (oe.ministerie === ministry) {
          effects.push({
            ...oe,
            icon,
            to: `/ministries/overigEffect/${fb.omschrijving}/${i}/${oe.ministerie.toLowerCase()}`,
            title,
            id: `oe-${fb.omschrijving}`,
            omschrijving: fb.omschrijving,
            read: fb.read,
            points: oe.puntenscore,
            oe: true,
            index: i,
          });
        }
      });
    }
    return effects;

  }, []);
}

function getOverigeInkomsten(feedback = [], gameState, minPolicyChoices = false) {
  const oi = feedback.reduce((overigeInkomsten, fb) => {
    if (fb.overigeInkomsten) {
      console.log(fb);
      if (fb.type === 'BELEIDSKEUZE' && gameState.difficulty === 'BASIC') {
        overigeInkomsten.push({
          ...fb.overigeInkomsten,
          icon: beleidskeuzes[fb['img']],
          title: `${__('sim_effect_vanuit')}: ${fb[`titel_oplossing_${gameState.language}`]}`,
        });
      } else {
        overigeInkomsten.push({
          ...fb.overigeInkomsten,
          icon: ministryIcons[fb.ministerie] || belastingIcons[fb.belastingsoort],
          title: `${__('sim_effect_vanuit')}: ${__(fb.ministerie || fb.belastingsoort)}`,
        });
      }
    }
    return overigeInkomsten;
  }, []);
  // Budget over van vorige ronde
  if (gameState.budgetLeft) {
    oi.push({
      icon: BudgetIcon,
      title: __('sim_budget_uit_vorige_spelronde'),
      tekst: __('sim_budget_uit_vorige_spelronde_omschrijving'),
      winstverliesglobas: gameState.budgetLeft,
    });
  }
  // Implement basic OI
  const basicOI = Defines.basicOI[gameState.difficulty][gameState.theme][gameState.currentRound] || {};
  let winstverliesglobas = 0;
  let tekst = __(basicOI.tekst);
  if (gameState.difficulty === 'PRO') {
    winstverliesglobas = String(basicOI.winstverliesglobas);
  } else {
    winstverliesglobas = Math.round((minPolicyChoices * 2) + basicOI.sum);
    tekst = tekst.replace('[x]', winstverliesglobas);
  }
  return [{
    icon: Grondstoffen,
    title: `${__('sim_grondstoffen')}: ${__(basicOI.title)}`,
    tekst: tekst,
    winstverliesglobas: winstverliesglobas,
  }].concat(oi);
}

function getEconomieFeedback(feedback = [], type) {
  feedback = feedback.filter(fb => fb.economie);
  if (type === 'profit') {
    return feedback.filter(fb => parseInt(fb.economie.winstverliesglobas, 10) >= 0);
  } else if (type === 'loss') {
    return feedback.filter(fb => parseInt(fb.economie.winstverliesglobas, 10) < 0);
  }
  return feedback;
}

function getPolicyChoiceFeedback(feedback = []) {
  return feedback.filter(fb => fb.type === 'BELEIDSKEUZE');
}

function getTitle(fb, lang) {
  let title = `${__('sim_effect_vanuit')}: `;
  if (fb.belastingNaam) {
    return title += __(fb.belastingsoort);
  }
  if (fb.type === 'BELASTING' && fb.niveau === 'Basic') {
    return title += __('sim_belastinginkomsten');
  }
  if (fb.type === 'BELEIDSKEUZE' && fb.niveau === 'Basic') {
    return title += fb[`titel_oplossing_${lang}`]
  }

  return title += __(fb.ministerie);
}

function getEconomieFeedbackFormatted(feedback, type, gameState) {
  const fb = getEconomieFeedback(feedback, type).sort(sorter);

  return fb.map(feedback => {
    let img = getImage(feedback.economie);

    if (img.indexOf('.jpg') !== -1) {
      img = img.slice(0, img.length - 4);
    } else if (img.indexOf('.jpeg') !== -1) {
      img = img.slice(0, img.length - 5);
    }

    return (
      <Link key={feedback.id} to={`/ministries/economie/${feedback.omschrijving}/${type}`}>
        <ListButton
          read={checkRead(feedback, false, 0, true)}
          bgColor='grey'
          title={getTitle(feedback, gameState.language)}
          desc={subString(getText(feedback.economie, gameState.language), 80, "...</p>")}
          icon={getIcon(feedback, true)}
          arrow={true}
          feedback={true}
          thumbnail={`${config.mediaUrl}${img.replace('+', '%2B')}.jpeg`}
          points={
            (type === 'profit' ?
              '+ '
              : null) +
            feedback.economie.winstverliesglobas
          }
          isGlobas={true}
        />
      </Link>
    );
  });
}

/**
 * Jesse zei dat het klaar was, dus ik heb deze regel gewoon weggehaald. Haha. Hij zei letterlijk: "het is klaar".
 * "Mijn naam is haas", aldus Jorrit.
 * @name renderMinistryFeedbackList
 * @param {array<{}>} feedback
 * @param {string} ministry
 * @return {array<jsx>} array of feedback items to render
 */
function renderMinistryFeedbackList(feedback = [], ministry, gameState, gameStateInput, currentItem = "") {
  const items = [].concat(
    getMinistryFeedbackItems(feedback, ministry, gameState, gameStateInput),
    getOverigeEffecten(feedback, ministry, gameState),
  ).sort(sorter);

  // Merge feedback and OE
  return items.map(fb => {
    let img = '';
    if (fb.img) {
      if (fb.img.indexOf('.jpg') !== -1) {
        img = fb.img.slice(0, fb.img.length - 4);
      } else if (fb.img.indexOf('.jpeg') !== -1) {
        img = fb.img.slice(0, fb.img.length - 5);
      } else {
        img = fb.img;
      }
    }
    console.log(fb.to);
    return (
      <Link key={fb.id} to={fb.to}>
        <ListButton
          read={checkRead(fb, fb.oe, fb.index)}
          bgColor='grey'
          title={fb.title}
          className={fb.omschrijving === currentItem ? 'show' : ''}
          desc={subString(getText(fb, gameState.language), 80, "...</p>")}
          icon={fb.icon}
          arrow={true}
          feedback={true}
          thumbnail={`${config.mediaUrl}${img.replace('+', '%2B')}.jpeg`}
          points={fb.points}
          disaster={fb.type === 'RAMP'}
        />
      </Link>
    )
  });
}

function getLowMinistryFeedbackLink(feedback = [], ministry, gameState, gameStateInput, debug = false) {
  const items = [].concat(
    getMinistryFeedbackItems(feedback, ministry.toUpperCase(), gameState, gameStateInput),
    getOverigeEffecten(feedback, ministry.toUpperCase(), gameState),
  ).sort(sorter);

  // Merge feedback and OE
  return items.map(fb => {
    return fb.to;
  });
}

function getFeedbackOverviewList(roundObject, self) {
  if (self.props.feedback.prevRoundFeedbackData) {
    let result = getFeedbackOverview(self.state.type, self.props.feedback.prevRoundFeedbackData).sort(sorter);
    let feedbackOverview = result.map(feedback => (
      <Link key={feedback.Omschrijving} to={'/ministries/overview/' + self.state.type + "/" + feedback.Ministerie.toLowerCase() + '/' + feedback.Omschrijving + '/'}>
        <ListButton
          read={checkRead(feedback)}
          bgColor='grey'
          title={__(getFeedbackTitle(feedback))}
          desc={subString(feedback.tekst_NL, 80, "...</p>")}
          icon={ministryIcons[feedback.Ministerie]}
          arrow={true}
          feedback={true}
          thumbnail={feedback.img}
          points={feedback.winstverliesglobas}
          isGlobas={true}
        />
      </Link>
    ));
    return feedbackOverview;
  }
}

function getFeedbackMinistery(feedback) {
  if (feedback.type === 'RAMP') {
    return feedback.ministerie;
  }

  return feedback.omschrijving.substr(3, 2);
}

function getFeedbackContainsEffectMinistery(feedback, ministry) {
  let key = 'Output: Ander ministerie [OE]';
  if (feedback[key] === "") {
    return null;
  }

  let suffix_underscore = "";
  let suffix = "";

  while ((key + suffix_underscore + suffix) in feedback && feedback[(key + suffix_underscore + suffix)] !== "") {
    const totalSuffix = suffix_underscore + suffix;

    if (feedback[`Output: Ander ministerie [OE]${totalSuffix}`] === ministry) {
      return {
        feedback: feedback,
        suffix: totalSuffix
      };
    }

    //Set feedback for next loop
    suffix_underscore = "_";
    if (suffix === "") {
      suffix = 2;
    } else {
      suffix = suffix += 1;
    }
  }
  return false;
}

function getMinisteryFeedback(min, feedbackList, unreadOnly = false) {
  if (!feedbackList) return 0;
  const result = feedbackList.filter(f => getFeedbackMinistery(f) === min);

  if (!unreadOnly) {
    return result;
  } else {
    return result.filter((current) => { return current.read['read'] !== true });
  }
}

function getFeedbackOverview(type, feedbackList, unreadOnly = false) {
  if (!feedbackList) return 0;

  let result;
  if (type === 'profit') {
    result = feedbackList.filter(f => f.winstverliesglobas > 0);
  } else {
    result = feedbackList.filter(f => f.winstverliesglobas < 0);
  }
  if (!unreadOnly) {
    return result;
  } else {
    return result.filter((current) => { return current.read['read'] !== true });
  }
}

function getMinistryEffectFeedback(min, feedbackList, unreadOnly = false) {
  if (!feedbackList) return 0;
  const result = feedbackList.map(f => getFeedbackContainsEffectMinistery(f, min)).filter(f => (f !== false && f !== null));
  if (!unreadOnly) {
    return result;
  } else {
    return result.filter((current) => { return current.feedback.read['fb' + current.suffix + "_read"] !== true });
  }
}

function getUnreadWinstGlobas(feedbackList) {
  let winstGlobasFeedback = getEconomieFeedback(feedbackList, 'profit');
  if (!winstGlobasFeedback.length) return [];
  return winstGlobasFeedback.filter(f => !checkRead(f, false, 0, true));
}


function getUnreadVerliesGlobas(feedbackList) {
  let verliesGlobasFeedback = getEconomieFeedback(feedbackList, 'loss');
  if (!verliesGlobasFeedback.length) return [];
  return verliesGlobasFeedback.filter(f => !checkRead(f, false, 0, true));
}

function getLastChar(feedback) {
  return feedback.toLowerCase().replace(/[^a-z]+/g, "").slice(-1);
}

function isDisaster(feedback) {
  return getLastChar(feedback) === 'r';
}

function ministryHasDisaster(min, feedback) {
  return feedback.prevRoundFeedbackData.find(fb => fb.ministerie === min && fb.type === 'RAMP');
}

function getFeedbackTitle(fb) {
  switch (fb.type) {
    case 'RAMP':
    case 'ALGEMEEN':
      return __('sim_budgetkeuze');
    case 'BELEIDSKEUZE':
      return __n('sim_beleidskeuze', 1);
    case 'STARTSITUATIE':
      return __('sim_startsituatie');
    case 'BELASTING':
      if (fb.niveau === 'Basic') {
        return __('sim_belastingkeuze');
      }
      return __(fb.belastingsoort);
    case 'OVERIGEFFECT':
      return __('sim_effect_vanuit') + ':';
    default:
      return '';
  }
}

/**
 *
 * @param feedbackList
 * @returns {{wg: *, vg: *}}
 */
function extractWgVg(feedbackList) {
  const wg = feedbackList.filter(f => f.winstverliesglobas && f.winstverliesglobas > 0);
  const vg = feedbackList.filter(f => f.winstverliesglobas && f.winstverliesglobas < 0);
  return { wg, vg };
}

/**
 * Check wether a ministry has been overfinanced or not
 * @param {String} ministry
 * @param {Object} gameState
 * @param {Number} roundNumber
 * @returns {Bool}
 */
function ministryOverfinanced(ministry, { difficulty, Rounds, prevRound }, roundNumber) {
  const round = typeof roundNumber === 'undefined' ? prevRound : Rounds[roundNumber];
  if (!round) return;

  let overfinanced = false;
  const states = round.GameStates.filter(gs => gs.type !== 'TEACHER_INTERVENTION' && gs.type !== 'BONUS_ASSIGNMENT');
  if (states && states.length > 0) {
    const lastGameState = states[states.length - 1];
    overfinanced = lastGameState.OneTimeEffect.overfinanced.includes(ministry);
  }
  return overfinanced;
  /**
   * Old code kept here in case the new part doesnt work.
   * Case was that the first onetimeeffect is the same as
   * the last one, so a ministry is overfinanced for two
   * rounds.
   */
  // return round.GameStates.find(
  //   gs => gs.OneTimeEffect.overfinanced
  //     && gs.OneTimeEffect.overfinanced.length
  //     && (gs.OneTimeEffect.overfinanced || []).includes(ministry)
  // );
}


function getMinistryARating(difficulty, ministryName, value, feedbackList) {
  const buckets = Defines.ministryValueBucket[difficulty][ministryName];
  let type = buckets.findIndex(val => value <= val);
  if (type === -1) type = buckets.length - 1;

  return type;
}

function getMinistryStatusBarRatings(min, gameState, gameStateInput) {
  const arr = [];
  const fn = val => value <= val;

  let value = 0;

  if (gameState.difficulty === 'PRO') {
    const sw = getSamenWerkingenForMinisterie(min, gameStateInput);

    //Loop through all rounds in the game (= 5 including startsituation)
    for (let c = 0; c < 5; c++) {

      //Return null when the round is bigger than the current round
      //Round has not been played yet.
      if (c >= gameState.currentRound) {
        arr.push(null);
        continue;
      }

      //Get the calculated ministryvalue
      value += gameState.Rounds[c].gameStateSum[`${min}_value`];

      //Get the defined status buckets for the ministry in question.
      let statusBuckets = Defines.statusBars.pro[min];

      //Determine whether the there is a collaboration array to use.
      //Always take the second array when collaboration because
      //international ministries have two arrays in the defines and
      //are the only ones that can collaborate.
      //If there is no collaboration, always take the first array.
      statusBuckets = sw ? statusBuckets[1] : statusBuckets[0];

      let pos = statusBuckets.findIndex(fn);

      //If value exceeds bucket
      if (value > statusBuckets[statusBuckets.length - 1]) {
        //Highest value
        value = statusBuckets[statusBuckets.length - 1] - 1;
        pos = statusBuckets.length;
      } else if (value < statusBuckets[0]) {
        //Lowest value
        value = statusBuckets[0];
        pos = 0;
      }

      let val;
      if (value === 0) {
        //A value of 0 is displayed as a negative so sets value to -0
        val = -0;
      } else if (pos < 5) {
        //Index of smaller than five indicates a negative value
        //Value has to be inverted to calculate the height of
        //the statusbar displayed so 5 is subtracted.
        val = 5 - pos;
      } else {
        //Index bigger than five indicates a positive value
        //Value has to be reduced by five to compensate
        //for the negative values in the array.
        val = pos - 5;
      }

      let percentage = (Math.abs(val) * 10);
      arr.push((pos <= 5 ? percentage * -1 : percentage));
    }
  } else {
    const sw = getSamenWerkingenForMinisterie(min, gameStateInput);

    //Loop through all rounds in the game (= 5 including startsituation)
    for (let c = 0; c < 5; c++) {

      //Return null when the round is bigger than the current round
      //Round has not been played yet.
      if (c >= gameState.currentRound) {
        arr.push(null);
        continue;
      }

      //Get the calculated ministryvalue
      value += gameState.Rounds[c].gameStateSum[`${min}_value`];

      //Get the defined status buckets for the ministry in question.
      let statusBuckets = Defines.statusBars.basic;

      //Determine whether the there is a collaboration array to use.
      //Always take the second array when collaboration because
      //international ministries have two arrays in the defines and
      //are the only ones that can collaborate.
      //If there is no collaboration, always take the first array.
      if (['ot', 'kn', 'oh', 'eh'].includes(min.toLowerCase())) {
        statusBuckets = (sw ? statusBuckets.national[0] : statusBuckets.international[0]);
      } else {
        statusBuckets = statusBuckets.national[0];
      }

      let pos = statusBuckets.findIndex(fn);
      //If value exceeds bucket
      if (value > statusBuckets[statusBuckets.length - 1]) {
        //Highest value
        value = statusBuckets[statusBuckets.length - 1] - 1;
        pos = statusBuckets.length;
      } else if (value < statusBuckets[0]) {
        //Lowest value
        value = statusBuckets[0];
        pos = 0;
      }

      let val;
      if (value === 0) {
        //A value of 0 is displayed as a negative so sets value to -0
        val = -0;
      } else if (pos < 5) {
        //Index of smaller than five indicates a negative value
        //Value has to be inverted to calculate the height of
        //the statusbar displayed so 5 is subtracted.
        val = 5 - pos;
      } else {
        //Index bigger than five indicates a positive value
        //Value has to be reduced by five to compensate
        //for the negative values in the array.
        val = pos - 5;
        if (['ot', 'kn', 'oh', 'eh'].includes(min.toLowerCase()) && sw < 1) {
          val = (val > 3 ? 3 : val);
        }

      }

      let percentage = (Math.abs(val) * 10);
      arr.push((pos <= 5 ? percentage * -1 : percentage));
    }
  }

  return arr;
}

function getSamenWerkingenForMinisterie(min, { ministryCollaborations }, roundOffset = -1) {
  if (Defines.i18nMinistries.indexOf(min) === -1) return 0;
  if (!ministryCollaborations[min]) return 0;
  return ministryCollaborations[min].length;
}

function getMinistryColor(difficulty, ministryName, { sumUpToPrevRound, currentRound }, gameStateInput, feedBackList, teaminfo = false) {
  const value = sumUpToPrevRound[`${ministryName}_value`] || 0;
  const roundPoints = sumUpToPrevRound[`${ministryName}_points`] || 0;
  if (((teaminfo && currentRound === 0) || (!teaminfo && currentRound === 1)) && value > 0 && Defines.i18nMinistries.indexOf(ministryName) !== -1)
    return 'green';

  let rating = getMinistryARating(difficulty, ministryName, value, feedBackList);
  let samenwerking = getSamenWerkingenForMinisterie(ministryName, gameStateInput);
  if (difficulty === 'basic') return getMinistryColorBasic(rating, samenwerking, ministryName);
  const { color } = Defines.ministryColors[ministryName].find(
    ({ c }) => c.find(
      ([status, points, sw]) => {
        if (Defines.i18nMinistries.indexOf(ministryName) !== -1) {
          if (rating < 1) {
            rating = 1;
          }
          if (rating > 2) {
            rating = 2;
          }
          return rating === status && (points === undefined || roundPoints < points) && (sw === undefined || (samenwerking || 0) <= sw);
        }
        return rating === status && (points === undefined || roundPoints < points);
      }
    )
  );
  return color;
}

function getMinistryColorBasic(rating, samenwerking, ministryName) {

  // Internationaal ministerie (samenwerken)
  if (Defines.i18nMinistries.indexOf(ministryName) > -1) {
    if (!samenwerking && rating <= 1) return 'red';
    if (samenwerking && rating <= 1) return 'lowOrange';
    if (!samenwerking && rating <= 3) return 'lowOrange';
    if (samenwerking && rating > 1) return 'green';
  } else {
    switch (rating) {
      case 0:
        return 'red';
      case 1:
        return 'lowOrange';
      case 2:
        return 'lowYellow';
      case 3:
      default:
        return 'green';
    }
  }
}


/**
 * Get subtitle from fb
 * @param {object} fb
 * @param {string} lang
 * @return {string} subtitle
 */
function getSubtitle(fb, lang = 'NL') {
  if (fb.type === 'BELEIDSKEUZE') {
    return fb[`titel_beleidskeuze_${lang}`] ? `: ${fb[`titel_beleidskeuze_${lang}`]}` : '';
  }

  if (fb.type === 'OVERIGEFFECT') {
    if (fb.belastingNaam) {
      return __(fb.belastingsoort);
    } else if (fb.ministerie) {
      return __(fb.ministerie);
    } else if (fb.niveau === 'Basic') {
      return __('sim_belastinginkomsten');
    }
  }

  return '';
}

/**
 * Get icon
 * @param {object} fb
 * @param {bool} economie
 * @return {*} img ref
 */
function getIcon(fb, economie = false) {
  if (fb.type === 'STARTSITUATIE') {
    if (window.innerWidth < 1024) return null;
    return ministryIcons[fb.ministerie];
  }
  if (fb.type === 'ALGEMEEN') {
    if (economie && fb.ministerie) {
      return ministryIcons[fb.ministerie];
    }

    return icons.globas;
  }
  if (fb.type === 'RAMP') {
    return icons.globas;
  }
  if (fb.type === 'BELEIDSKEUZE') {
    if (fb.niveau === 'Basic') {
      return icons.beleidskeuze;
    } else if (economie && fb.ministerie) {
      return ministryIcons[fb.ministerie];
    }

    return icons.beleidskeuze;
  }
  if (fb.type === 'BELASTING') {
    if ((fb.niveau === 'Basic')) {
      return belastingIcons['LB'];
    }

    return belastingIcons[fb.belastingsoort];
  }
  if (fb.type === 'OVERIGEFFECT') {
    return ministryIcons[fb.ministerie] || (belastingIcons[fb.belastingsoort] ? belastingIcons[fb.belastingsoort] : belastingIcons['LB']);
  }

  return ministryIcons[fb.ministerie];
}

/**
 * get costs
 * @param {object} fb
 * @param {object} gameState
 * @return {number} cost
 */
function getCosts(fb, gameState) {
  let costs = fb.kosten_globas;
  if (Array.isArray(costs)) {
    if (checkIfCollab(fb)) {
      costs = fb.kosten_globas[1];
    } else {
      costs = fb.kosten_globas[0];
    }
  }
  if (fb.type === 'BELEIDSKEUZE') {
    return costs || 0;
  }
  if (fb.type === 'BELASTING') {
    return gameState.prevRoundSum[`${fb.belastingsoort}_globas`] || 0;
  }
  if (!gameState || !gameState.prevRound.gameStateInput) return 0;
  return gameState.prevRound.gameStateInput.ministerieGlobaVerdeling[fb.ministerie] || 0;
}

/**
 * check if item contains collaboration
 * @param {object} fb
 * @return {bool} whether item contains collaboration or nah.
 */
function checkIfCollab(fb) {
  if (fb.samenwerking.reduce((total, collab) => total + collab) > 0) {
    return true;
  }
  return false;
}

/**
 * get collabs
 * @param {object} fb
 * @param {object} gameStateInput
 * @return {array<string>} array of group theme names
 */
function getCollaborations(fb, gameStateInput) {
  if (fb.type === 'BELEIDSKEUZE') {
    return gameStateInput.policyChoiceCollaborations[fb.omschrijving.split('SW')[0]] || [];
  }

  const order = ['red', 'orange', 'yellow', 'green', 'blue', 'purple'];
  let res = gameStateInput.ministryCollaborations[fb.ministerie];
  if (res) {
    res.sort(function (a, b) {
      if (order.indexOf(a.toLowerCase()) > order.indexOf(b.toLowerCase())) {
        return 1;
      }
      if (order.indexOf(a.toLowerCase()) < order.indexOf(b.toLowerCase())) {
        return -1;
      }
      return 0;
    });
  }


  return res || [];
}

/**
 * get points
 * @param {object} fb
 * @param {object} gameState
 * @param {number} collaborations
 * @return {number} points
 */
function getPoints(fb, gameState, collaborations = 0) {
  if (collaborations && Array.isArray(fb.puntenscore) && fb.puntenscore[0].match(/^SW\d-\d=\d*$/)) {
    return getCollaborationPoints(fb.puntenscore, collaborations);
  }
  if (['ALGEMEEN', 'RAMP'].indexOf(fb.type) !== -1) {
    return gameState.prevRoundSum[`${fb.ministerie}_points_raw`];
  }
  if (fb.type === 'BELASTING') {
    return gameState.prevRoundSum[`${fb.belastingsoort}_points`] || 0;
  }
  return fb.puntenscore || 0;
}

/**
 * get image text
 * @param {object} fb
 * @param {string} lang
 * @return {string} image text
 */
function getImageText(fb, lang = 'NL') {
  return fb[`img_tekst_${lang}`];
}

/**
 * get image
 * @param {object} fb
 * @returns {string} img src
 */
function getImage(fb) {
  if (fb) {
    if (fb.img) {
      return fb.img.replace('+', '%2B');
    } else if (fb.omschrijving) {
      return fb.omschrijving.replace('+', '%2B');
    }
  }

  return '';
}

/**
 * get text
 * @param {object} fb
 * @param {string} lang
 * @return {string} text
 */
function getText(fb, lang = 'NL') {
  return fb[`tekst_${lang}`] || fb.tekst;
}

/**
 * get OE
 * @param {object} fb
 * @return {array<{}>} overige effecten
 */
function getSideEffects(fb) {
  return fb.overigeEffecten || [];
}

function getUnreadCount(ministry, prevRoundFeedbackData) {
  return getMinisteryFeedback(ministry, prevRoundFeedbackData, true).length
    + getOverigeEffecten(prevRoundFeedbackData, ministry)
      .filter(i => !checkRead(i, i.oe, i.index, i.economie))
      .length
}

function getPolicyChoiceCollaborations(s, roundNum = s.myState.roundNumber - 1) {
  const round = s.states[roundNum]; // previous round number
  if (round && round[s.groupId]) {
    return Object.keys(round[s.groupId].policyChoices || {})
      .filter(pcKey => round[s.groupId].policyChoices[pcKey].collaboration.length)
      .reduce((collabs, pcKey) => {
        const myCollabs = round[s.groupId].policyChoices[pcKey].collaboration;
        myCollabs.forEach((color) => {
          const id = Object.keys(round).find(id => round[id].theme === color);
          const group = round[id];
          if (group && group.policyChoices[pcKey] && group.policyChoices[pcKey].collaboration.includes(round[s.groupId].theme)) {
            const keyWithoutSW = pcKey.split('SW')[0];
            if (!collabs[keyWithoutSW]) collabs[keyWithoutSW] = [];
            collabs[keyWithoutSW].push(color);
          }
        });
        return collabs;
      }, {});
  }
}

function reduceMinistryCollabs(mKey, s, roundNumber = s.myState.roundNumber - 1) {
  const key = `${mKey}SW`;

  if (roundNumber > 0 && s.states[roundNumber][s.groupId] && s.states[roundNumber][s.groupId].ministerieGlobaVerdeling
    && Array.isArray(s.states[roundNumber][s.groupId].ministerieGlobaVerdeling[key])) {
    return s.states[roundNumber][s.groupId].ministerieGlobaVerdeling[key].reduce((arr, val) => {
      const group = Object.keys(s.states[roundNumber]).find(id => s.states[roundNumber][id].theme === val);
      if (group && Array.isArray(s.states[roundNumber][group].ministerieGlobaVerdeling[key]) && s.states[roundNumber][group].ministerieGlobaVerdeling[key].includes(s.myState.theme)) { // assuming you won't change theme after one round :P
        if (s.states[roundNumber][group].ministerieGlobaVerdeling[mKey] === s.states[roundNumber][s.groupId].ministerieGlobaVerdeling[mKey]) {
          arr.push(val);
        }
      }
      return arr;
    }, []);
  }
  return [];
}

const debitCreditIcons = {
  stortenSpaargeld: Bank,
  opnemenSpaargeld: Bank,
  lenenVanLand: LoanLand,
  lenenAanLand: LoanToLand,
  lenenVanBank: Bank,
  schenkAanland: Gift,
};

function simulateDebitCreditOutcome(obj) {
  const outcome = calculateDebitCreditOutcome(obj);
  if (!outcome) return null;
  return {
    newDebt: {
      ...obj,
      payed: (obj.payed || 0) + outcome.amount,
      round: obj.round + 1
    },
    outcome
  };
}

function calculateDebitCreditOutcome({ amount, payed = 0, interest, key, round, otherCountry, interestInPercent, bankDisabledPayout }, currentRound = -1, bankDisabled = false) {
  if (amount + interest === payed) return null; //old

  let roundMethod = amount < 0 ? Math.floor : Math.ceil;
  const ret = { icon: debitCreditIcons[key], key };

  if (otherCountry) {
    ret.title = amount > 0 ? __('sim_te_betalen_aan_land_procent') : __('sim_te_ontvangen_van_land');
    ret.title = ret.title.replace('%', __(otherCountry));
    ret.icon = getCountryIcon(otherCountry); //groupIcons[__(otherCountry).toLocaleLowerCase()];
  } else {
    roundMethod = payed > 0 ? Math.floor : Math.ceil;
    ret.title = amount > 0 ? __('sim_te_betalen_aan_bank') : __('sim_te_ontvangen_van_bank');
  }

  interest = interestInPercent ? amount * interest : interest;
  if (interest > 0 && interest < 1) interest = 1;
  let halfInterest = interest / 2;
  if (halfInterest > 0 && halfInterest < 1) halfInterest = 1;

  if (currentRound >= 4 && payed === 0 && key !== 'stortenSpaargeld') {
    ret.amount = roundMethod(amount + interest);
    return ret;
  }

  if (key === 'stortenSpaargeld') {
    if (bankDisabledPayout || bankDisabled) {
      ret.amount = roundMethod(amount + interest);
    } else {
      ret.amount = roundMethod(interest);
    }
    ret.title = ret.amount > 0 ? __('sim_te_betalen_aan_bank') : __('sim_te_ontvangen_van_bank');
    return ret;
  }
  
  if (bankDisabled) {
    ret.amount = (amount + interest) - payed;
    ret.amount = roundMethod(ret.amount);
    return ret;
  }

  // always payed back in two rounds, so any time anything has been payed back, we'll pay the rest
  ret.amount = payed ?
    (amount + interest) - payed :
    ((amount / 2) + halfInterest);
  ret.amount = roundMethod(ret.amount);
  return ret;
}

function calculateDebitCreditOutcomeBasic({ amount, payed = 0, interest, key, round, otherCountry, interestInPercent, bankDisabledPayout }, bankDisabled = false) {
  if (amount + interest === payed) return null; //old
  const ret = { icon: debitCreditIcons[key], key };

  if (otherCountry) {
    ret.title = amount > 0 ? __('sim_te_betalen_aan_land_procent') : __('sim_te_ontvangen_van_land');
    ret.title = ret.title.replace('%', __(otherCountry));
    ret.icon = groupIcons[__(otherCountry).toLocaleLowerCase()];
  } else {
    ret.title = amount > 0 ? __('sim_te_betalen_aan_bank') : __('sim_te_ontvangen_van_bank');
  }

  interest = interestInPercent ? amount * interest : interest;
  if (interest > 0 && interest < 1) interest = 1;

  if (round >= 3) {
    ret.amount = amount + interest;
    return ret;
  }

  if (key === 'stortenSpaargeld') {
    if (bankDisabledPayout || bankDisabled) {
      ret.amount = amount + interest;
    } else {
      ret.amount = interest;
    }
    ret.title = ret.amount > 0 ? __('sim_te_betalen_aan_bank') : __('sim_te_ontvangen_van_bank');
    return ret;
  }

  // always payed back in two rounds, so any time anything has been payed back, we'll pay the rest
  ret.amount = amount + interest;
  return ret;
}

function getPolicyChoiceTotalPoints(fb) {
  return getPolicyChoiceFeedback(fb).reduce((points, item) => {
    points += (item.puntenscore || 0);
    return points;
  }, 0);
}

export {
  getFeedback,
  getFeedbackMinistery,
  getMinisteryFeedback,
  getMinistryEffectFeedback,
  getLowMinistryFeedbackLink,
  getEconomieFeedbackFormatted,
  getUnreadWinstGlobas,
  getUnreadVerliesGlobas,
  renderMinistryFeedbackList,
  extractWgVg,
  subString,
  getFeedbackTitle,
  isDisaster,
  getLastChar,
  getFeedbackOverview,
  getFeedbackOverviewList,
  checkRead,
  getMinistryARating,
  getMinistryColor,
  getMinistryStatusBarRatings,
  getSamenWerkingenForMinisterie,
  getOverigeEffecten,
  getSubtitle,
  getIcon,
  getCosts,
  checkIfCollab,
  getCollaborations,
  getPoints,
  getImageText,
  getImage,
  getText,
  getSideEffects,
  ministryOverfinanced,
  getEconomieFeedback,
  getUnreadCount,
  getPolicyChoiceCollaborations,
  reduceMinistryCollabs,
  ministryHasDisaster,
  getOverigeInkomsten,
  simulateDebitCreditOutcome,
  calculateDebitCreditOutcome,
  calculateDebitCreditOutcomeBasic,
  unreadCount,
  getPolicyChoiceFeedback,
  getPolicyChoiceTotalPoints,
};



// WEBPACK FOOTER //
// ./src/helpers/feedbackHelpers.js